import type { PageField, PageView } from '~/pages/AskRichardUnified/page/pageContract';
import { validatePageFilters } from '~/pages/AskRichardUnified/page/pageFilters';

import { FILTER_DEFINITIONS, type ManifestFilters, type Measures, REPORT_VIEWS, type ReportView } from './model';

const VISIBLE_ROWS = 25;

type PageSection = {
  title: string;
  rows: Array<{ label: string; values: Record<string, number | null> }>;
  truncated: boolean;
};

export function ltlpManifestPageView(input: {
  ready: boolean;
  filters: ManifestFilters;
  options: Record<string, Array<string>>;
  period: string;
  totals: Measures | null;
  section: PageSection | null;
}): PageView {
  const { filters, options } = input;
  const filterSchema: Record<string, PageField> = {
    reportView: { type: 'enum', values: REPORT_VIEWS.map((view) => view.value) },
    from: { type: 'date' },
    to: { type: 'date' },
    ...Object.fromEntries(
      FILTER_DEFINITIONS.map(({ key }) => [key, { type: 'enum', values: [null, ...(options[key] ?? [])] }]),
    ),
  };
  const section = input.section;
  return {
    title: 'LTLP Manifest',
    ready: input.ready,
    dataMode: 'live reporting.sales',
    filters: {
      reportView: filters.reportView,
      from: filters.from,
      to: filters.to,
      ...Object.fromEntries(
        FILTER_DEFINITIONS.map(({ key }) => [
          key,
          filters.selections[key].length === 1 ? filters.selections[key][0] : null,
        ]),
      ),
    },
    filterSchema,
    activeSelections: Object.fromEntries(
      FILTER_DEFINITIONS.filter(({ key }) => filters.selections[key].length).map(({ key }) => [
        key,
        filters.selections[key],
      ]),
    ),
    period: input.period,
    totals: input.totals,
    visibleSections: section
      ? [
          {
            title: section.title,
            totalRows: section.rows.length,
            rows: section.rows.slice(0, VISIBLE_ROWS).map((row) => ({ label: row.label, ...row.values })),
            truncated: section.truncated || section.rows.length > VISIBLE_ROWS,
          },
        ]
      : [],
    definitions: {
      source:
        'lux-analytics-prod.reporting.sales, LTLP only (product_bk bedbank_marriott); queried live, rebuilt daily.',
      dates:
        'Sydney transaction dates. Bookings opens on last week (Monday to Sunday); Vendor Summary on this year to yesterday.',
      measures:
        'Gross sales, margin and cost sum gross_sales_aud, margin_aud and cost_sales_aud; packages and nights sum number_of_packages and number_of_nights.',
      filters:
        'Each filter accepts one value or null for all values here. Exclude Old Margin Maxed Offers is False only for margin-maxed rows that are not the item’s latest rebooking.',
    },
  };
}

/** Applies a validated askRichard edit: one value per filter, or null for every value. */
export function editFromBridge(
  changes: Record<string, unknown>,
  view: PageView,
  filters: ManifestFilters,
): Partial<ManifestFilters> {
  validatePageFilters(changes, view.filterSchema);
  const next: Partial<ManifestFilters> = {};
  if ('reportView' in changes) {
    next.reportView = changes.reportView as ReportView;
  }
  if ('from' in changes) {
    next.from = changes.from as string;
  }
  if ('to' in changes) {
    next.to = changes.to as string;
  }
  const selections = { ...filters.selections };
  let changed = false;
  for (const { key } of FILTER_DEFINITIONS) {
    if (!(key in changes)) {
      continue;
    }
    const value = changes[key] as string | null;
    selections[key] = value === null ? [] : [value];
    changed = true;
  }
  if (changed) {
    next.selections = selections;
  }
  return next;
}
